import { DateTime } from 'luxon';

export class Calculator {
  /**
   * @param {AppState} appState 
   */
  constructor(appState) {
    /** @type {AppState} */
    this.appState = appState;
  }

  /**
   * @param {number} index 
   * @returns {PeriodDuration}
   * @throws {RangeError} If configuration index does not exist.
   */
  lengthFromSelectIndex(index) {
    const length = this.appState.getConfig('periods')[index];
    if (!length) {
      throw new RangeError(`Unknown select index: ${index}`);
    }

    return length;
  }

  /**
   * 
   * @param {DateTime} date 
   * @param {number} selectIndex 
   * @param {boolean} special 
   * @returns {Entry}
   * @throws {TypeError} If provided date is not a valid Luxon DateTime object.
   */
  createEntry(date, selectIndex, special) {
    if (!DateTime.isDateTime(date) || !date.isValid) {
      throw new TypeError('date must be a valid Luxon DateTime');
    }
    /*
    const length = this.appState.getConfig('periods')[period];
    if (!length) {
      throw new RangeError(`Unknown period: ${period}`);
    }
    */
    return {
      date,
      len: this.lengthFromSelectIndex(selectIndex),
      special: special,
    };
  }

  /**
   * 
   * @returns {EntryList}
   */
  createDemoEntries() {
    return this.appState.demo_entries
      .map(({ date, selectIndex, special }) => ({
        date: DateTime.fromISO(date),
        len: this.lengthFromSelectIndex(selectIndex),
        special,
      }));
  }

  /**
   * Sort entries chronologically and calculate the first expiry for every
   * offence. No input objects are mutated.
   * @param {EntryList} entries
   * @returns {ForholdRecord[]}
   */
  calculateInitialExpiry(entries) {
    let specialCount = 0;

    return [...entries]
      .sort((a, b) => a.date.toMillis() - b.date.toMillis())
      .map((entry, index) => {
        const specialFlag = specialCount;
        if (entry.special) {
          specialCount++;
        }

        return {
          y: index,
          range: [entry.date, entry.date.plus(entry.len)],
          offset: entry.date.plus(entry.len),
          duration: entry.len,
          isSpecial: entry.special,
          isSpecialRecurrence: (entry.special) ? Boolean(specialFlag) : false,
          isRecurrence: index > 0,
          recurrenceNo: index,
          isLastExpiry: false,
        }
      });
  }

  /**
   * 
   * @param {ForholdRecord[]} expiry 
   * @returns {ForholdRecord[]}
   */
  markLastExpiry(expiry) {
    if (!expiry || expiry.length === 0) return expiry;

    const last = expiry.reduce(
      (latest, current) => {
        const latestDate = latest?.range?.[1];
        const currentDate = current?.range?.[1];

        if (!currentDate) return latest;
        if (!latestDate) return current;
        
        return latestDate > currentDate ? latest : current
      },
      expiry[0],
    );

    if (last) {
      last.isLastExpiry = true;
    }

    return expiry;
  }

  /**
   * Build the recurrence overlays used by Chart.js.
   *
   * The staircase algorithm is kept equivalent to the original implementation,
   * but works on fresh derived objects and avoids mutating the caller's data.
   * @param {ForholdRecord[]} expiry
   * @returns {KarensRecord[]}
   */
  makeRecurrenceExtras(expiry) {
    const noOfRecurrences = Math.max(0, expiry.length - 1);
    /** @type {KarensRecord[]} */
    const extras = [];

    for (let column = 0; column < noOfRecurrences; column++) {
      /** @type {DateRangeList} */
      const data = [];

      for (let row = 0; row < expiry.length; row++) {
        const current = expiry[row];
        const paintsCell = current.isLastExpiry || row - column > 0;


        if (!paintsCell) {
          data.push([null, null]);
          continue;
        }

        const thisColumn = expiry[column + 1]; 
      
        /** @type {import('luxon').DurationLike} */
        const extraDuration = (thisColumn?.isSpecialRecurrence)
          ? this.appState.applyModifiers(
              'duration:alter',
              this.appState.getConfig('special_ext'),
              thisColumn?.isSpecialRecurrence
            )
          : this.appState.applyModifiers(
              'duration:alter',
              thisColumn?.duration,
              thisColumn?.isSpecialRecurrence
            )
          //thisColumn?.duration;

        const from = current.offset;
        const to = from.plus(extraDuration);

        current.offset = to;

        data.push([from, to]);
      }

      extras.push({
        label: 'Gentagelse',
        data,
      });
    }

    return extras;
  }

  /**
   * Calculates all data needed by the table footer and chart.
   * @param {EntryList} entries
   * @returns {CompleteRecord | null}
   */
  calculate(entries) {
    if (!entries?.length) {
      return null;
    }

    const expiry = this.markLastExpiry(this.calculateInitialExpiry(entries));
    const extras = this.makeRecurrenceExtras(expiry);
    const lastExpiry = expiry.find((item) => item.isLastExpiry); 

    return {
      expiry,
      extras,
      finalExpiry: lastExpiry ? lastExpiry.offset: null
    };
  }
}